import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, List as ListIcon, Eye } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import StatusBadge from "../../components/StatusBadge";
import { useListLposQuery } from "../../stockApi";
import { useCanCreateLpo } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "REVISED_SUBMITTED", label: "Revised - Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "PARTIALLY_RECEIVED", label: "Partially Received" },
  { value: "FULLY_RECEIVED", label: "Fully Received" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const PURPOSE_OPTIONS = [
  { value: "", label: "All Purposes" },
  { value: "GENERAL_REPLENISHMENT", label: "General Replenishment" },
  { value: "VEHICLE_MAINTENANCE", label: "Vehicle Maintenance" },
  { value: "CUSTOMER_SPECIFIC", label: "Customer Specific" },
  { value: "OTHER", label: "Other" },
];

const LpoList = () => {
  const canCreate = useCanCreateLpo();
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [purposeType, setPurposeType] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListLposQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    purposeType: purposeType || undefined,
    search: search || undefined,
    pageSize: 100,
  });

  const columns = [
    {
      title: "LPO No",
      dataIndex: "lpoNumber",
      render: (value, record) => <Link to={paths.stockLpoDetail(record.recordGuid)}>{value}</Link>,
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Supplier", dataIndex: "supplierName" },
    { title: "Purpose", dataIndex: "purposeType", render: (v) => v?.replaceAll("_", " ") },
    { title: "Date", dataIndex: "lpoDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Net Amount", dataIndex: "netAmount", render: (v) => Number(v || 0).toFixed(2) },
    { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} /> },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button
          type="button"
          className="btn btn-link p-2"
          title="View"
          onClick={() => navigate(paths.stockLpoDetail(record.recordGuid))}
        >
          <Eye size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Local Purchase Orders</h4>
              <h6>Create, approve, and track LPOs</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={() => navigate(paths.stockLpoPendingVehicleRequests)}
              >
                <ListIcon className="me-2" size={16} />
                Pending Vehicle Requests
              </button>
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockLpoNew)}>
                <PlusCircle className="me-2" size={16} />
                New LPO
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search LPO No or Supplier"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select className="form-control" value={purposeType} onChange={(e) => setPurposeType(e.target.value)}>
                  {PURPOSE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={data?.items || []} rowKey="recordGuid" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LpoList;
